'use client';

import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { DEFAULT_GEO_RANGE } from '@/constants/numbers';
import { useNotificationsDispatch } from '@/features/notifications/notificationsContext';
import { fetchPlacesEffect } from '@/features/places/state/effects';
import { PlaceDTO } from '@/features/places/types/places.dto';
import {
  createStoreSelectorContext,
  SelectorStoreContextValue,
  useContextStore
} from '@/lib/createSelectorContext';
import { BaseState, StateSnapshot } from '@/types';
import * as actions from './actions';

export type PlacesAction = ReturnType<(typeof actions)[keyof typeof actions]>;

export interface PlacesState extends BaseState<StateSnapshot<PlacesAction, PlacesState>> {
  query: string;
  items: PlaceDTO[];
  /** in km - default 20 - max 300 */
  rangeFrom: number;
  rangeTo: number;
  isLoading: boolean;
  nextRangeCount: number | null;
}

const initialState: PlacesState = {
  query: '',
  items: [],
  rangeFrom: 0,
  rangeTo: DEFAULT_GEO_RANGE,
  isLoading: false,
  nextRangeCount: null
} as unknown as PlacesState;

const PlacesContext = createContext<SelectorStoreContextValue<PlacesState, PlacesAction> | null>(
  null
);

const reducer = (state: PlacesState, action: PlacesAction): PlacesState => {
  const newState: PlacesState = {
    ...state,
    snapshot: { action, oldState: { ...state, items: [] } }
  };

  switch (action.type) {
    case actions.placesFetch.type:
      return {
        ...newState,
        query: action.payload.query,
        isLoading: true
      };
    case actions.placesFetchMore.type:
      return {
        ...newState,
        isLoading: true
      };
    case actions.placesSet.type:
      return {
        ...newState,
        ...action.payload,
        // ? reset state if query is changed
        rangeFrom: 0,
        rangeTo: DEFAULT_GEO_RANGE,
        isLoading: false
      };
    case actions.placesAdd.type:
      const { items, ...rest } = action.payload;
      const uniqItems = Object.values(
        [...newState.items, ...items].reduce(
          (acc, place) => {
            acc[place.code] = place;
            return acc;
          },
          {} as Record<string, PlaceDTO>
        )
      );
      return {
        ...newState,
        ...rest,
        items: uniqItems,
        isLoading: false
      };
    case actions.placesFetchFailed.type:
      return {
        ...newState,
        isLoading: false
      };
    default:
      return newState;
  }
};

export const PlacesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState });
  const notificationDispatch = useNotificationsDispatch();

  useEffect(() => {
    if (
      state.snapshot?.action.type === actions.placesFetch.type ||
      state.snapshot?.action.type === actions.placesFetchMore.type
    ) {
      fetchPlacesEffect({ state, dispatch, notificationDispatch });
    }
  }, [state]);

  const contextValue = useContextStore(state, dispatch);

  return <PlacesContext.Provider value={contextValue}>{children}</PlacesContext.Provider>;
};

const { useSelector, useDispatch } = createStoreSelectorContext<PlacesState, PlacesAction>(
  PlacesContext,
  'PlacesContext'
);

export const usePlacesDispatch = useDispatch;
export const usePlacesItems = () => useSelector((state) => state.items);
export const usePlacesIsLoading = () => useSelector((state) => state.isLoading);
export const usePlacesNextRangeCount = () => useSelector((state) => state.nextRangeCount);
